import { useEffect, useRef } from 'react'
import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { Address, zeroAddress } from 'viem'
import { UserPoints } from '../types'
import { userPointsQueryOptions } from './query'
import { useWalletAuthSession } from '@/domain/hooks/useWalletAuthSession'

export type UseUserPointsResult = Pick<UseQueryResult<UserPoints>, 'data' | 'isLoading' | 'error'>

export function useUserPoints({ account, chainId }: { account?: Address; chainId: number }): UseUserPointsResult {
  const attemptedAddressRef = useRef<string | null>(null)
  const {
    ensureSession,
    error: walletSessionError,
    hasMatchingSession,
    isAuthenticating,
    isClearingSession,
    isLoadingSession,
  } = useWalletAuthSession()

  const normalizedAccount = account?.toLowerCase() ?? null

  useEffect(() => {
    if (!normalizedAccount) {
      attemptedAddressRef.current = null
      return
    }

    if (hasMatchingSession) return
    if (isLoadingSession || isAuthenticating || isClearingSession) return
    if (attemptedAddressRef.current === normalizedAccount) return

    attemptedAddressRef.current = normalizedAccount
    void ensureSession().catch(() => undefined)
  }, [ensureSession, hasMatchingSession, isAuthenticating, isClearingSession, isLoadingSession, normalizedAccount])

  const result = useQuery({
    ...userPointsQueryOptions({ account: account ?? zeroAddress, chainId }),
    enabled: !!account && hasMatchingSession,
  })

  return {
    data: result.data,
    isLoading: isLoadingSession || isAuthenticating || isClearingSession || result.isLoading,
    error: (walletSessionError as UseQueryResult<UserPoints>['error']) ?? result.error,
  }
}
